import {
    ImagePlus,
    Maximize2,
    Mic,
    Minimize2,
    SendHorizontal,
    Trash2,
} from "lucide-react";
import { motion } from "motion/react";
import { type ReactNode, useRef } from "react";
import { Button } from "../../button/Button";
import { Pressable } from "../../Pressable";
import { useChatInputContext } from "./Context";
import { chatInputMotionTransition } from "./motion";
import { VoiceRecordingVisualizer } from "./VoiceRecording";

const chatInputButtonClass = "py-8 px-16";

const buttonAnimationVariants = {
    topRow: {
        actions: {
            hidden: {
                opacity: 0,
                width: 0,
            },
            visible: {
                opacity: 1,
                width: "auto",
            },
        },
    },
    bottomRow: {
        wrapper: {
            hidden: {
                height: 0,
                transition: {
                    ...chatInputMotionTransition,
                    staggerChildren: 0.05,
                },
            },
            visible: {
                height: "auto",
                transition: {
                    ...chatInputMotionTransition,
                    staggerChildren: 0.05,
                    delayChildren: 0.05,
                },
            },
        },
        sendButton: {
            hidden: {
                opacity: 0,
                translateX: 32,
            },
            visible: {
                opacity: 1,
                translateX: 0,
            },
        },
        fileButton: {
            hidden: {
                opacity: 0,
                translateX: -32,
            },
            visible: {
                opacity: 1,
                translateX: 0,
            },
        },
    },
};

export function InactiveInputActions() {
    const { onFileSelected, onSendVoice } = useChatInputContext();

    if (!onFileSelected && !onSendVoice) {
        return null;
    }

    return (
        <MainActionButtons>
            <div className="flex flex-row gap-8">
                {onFileSelected ? <FileInputButton /> : null}
                {onSendVoice ? <VoiceRecordButton /> : null}
            </div>
        </MainActionButtons>
    );
}

export function ActiveInputActions() {
    const { canSend, isActive, labels, onFileSelected, sendMessage } =
        useChatInputContext();

    return (
        <motion.div
            className="w-full shrink-0 overflow-hidden"
            variants={buttonAnimationVariants.bottomRow.wrapper}
            initial="hidden"
            animate={isActive ? "visible" : "hidden"}
            transition={chatInputMotionTransition}
        >
            <div className="flex w-full flex-row items-center justify-between gap-4">
                {onFileSelected ? (
                    <motion.div variants={buttonAnimationVariants.bottomRow.fileButton}>
                        <FileInputButton />
                    </motion.div>
                ) : null}

                <motion.div
                    className={onFileSelected ? undefined : "ml-auto"}
                    variants={buttonAnimationVariants.bottomRow.sendButton}
                >
                    <Button
                        variant="text"
                        aria-label={labels.send}
                        className={chatInputButtonClass}
                        data-chat-input-button="true"
                        isDisabled={!canSend}
                        onPress={sendMessage}
                    >
                        <SendHorizontal aria-hidden="true" className="size-24" />
                    </Button>
                </motion.div>
            </div>
        </motion.div>
    );
}

export function ExpandInputAction() {
    const { isExpanded, labels, toggleExpanded } = useChatInputContext();

    return (
        <Pressable
            aria-label={isExpanded ? labels.collapse : labels.expand}
            aria-expanded={isExpanded}
            className="p-12"
            tabIndex={-1}
            onPointerDown={(event) => {
                // Hold textarea focus on iOS. Do not toggle here: shrinking on
                // pointerdown lets the same tap land on the iframe behind.
                event.preventDefault();
            }}
            onMouseDown={(event) => {
                event.preventDefault();
            }}
            onPress={toggleExpanded}
        >
            {isExpanded ? (
                <Minimize2 aria-hidden="true" className="size-24 text-text-tertiary" />
            ) : (
                <Maximize2 aria-hidden="true" className="size-24 text-text-tertiary" />
            )}
        </Pressable>
    );
}

export function VoiceRecordingActions() {
    const { cancelRecording, labels, sendRecording } = useChatInputContext();

    return (
        <div className="flex w-full flex-row items-center gap-4 px-8 py-8">
            <Button
                variant="text"
                aria-label={labels.discardVoice}
                className={chatInputButtonClass}
                data-chat-input-button="true"
                onPress={cancelRecording}
            >
                <Trash2 aria-hidden="true" className="size-24 text-text-primary" />
            </Button>

            <VoiceRecordingVisualizer />

            <Button
                variant="text"
                aria-label={labels.sendVoice}
                className={chatInputButtonClass}
                data-chat-input-button="true"
                onPress={sendRecording}
            >
                <SendHorizontal aria-hidden="true" className="size-24" />
            </Button>
        </div>
    );
}

interface AnimatedButtonRowProps {
    children: ReactNode;
}

function MainActionButtons({ children }: AnimatedButtonRowProps) {
    const { isActive } = useChatInputContext();

    return (
        <motion.div
            className="flex shrink-0 flex-row self-center overflow-hidden"
            variants={buttonAnimationVariants.topRow.actions}
            initial="visible"
            animate={isActive ? "hidden" : "visible"}
            transition={chatInputMotionTransition}
        >
            {children}
        </motion.div>
    );
}

function FileInputButton() {
    const { acceptedFileTypes, labels, onFileSelected } = useChatInputContext();
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!onFileSelected) {
        return null;
    }

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept={acceptedFileTypes?.join(",")}
                className="hidden"
                onChange={(event) => {
                    const file = event.currentTarget.files?.[0];
                    event.currentTarget.value = "";

                    if (file) {
                        onFileSelected(file);
                    }
                }}
            />
            <Button
                variant="text"
                aria-label={labels.attachFile}
                className={chatInputButtonClass}
                data-chat-input-button="true"
                onPress={() => {
                    fileInputRef.current?.click();
                }}
            >
                <ImagePlus aria-hidden="true" className="size-24 text-text-tertiary" />
            </Button>
        </>
    );
}

function VoiceRecordButton() {
    const { labels, startRecording } = useChatInputContext();

    return (
        <Button
            variant="text"
            aria-label={labels.recordVoice}
            className={chatInputButtonClass}
            data-chat-input-button="true"
            onPress={startRecording}
        >
            <Mic aria-hidden="true" className="size-24 text-text-tertiary" />
        </Button>
    );
}
